import { createContext, useContext } from 'react'

/** @type {import('react').Context<null | Record<string, unknown>>} */
export const AppShellLayoutContext = createContext(null)

/** @returns {Record<string, unknown>} */
export function useAppShellLayoutContext() {
  const ctx = useContext(AppShellLayoutContext)
  if (!ctx) {
    throw new Error('useAppShellLayoutContext must be used within AppShell')
  }
  return ctx
}

/**
 * Like `useAppShellLayoutContext`, but returns null when the caller is not
 * inside an AppShell (e.g. a storybook render or a unit test that wraps only
 * one phase). Components that render chat cards but do not require the
 * surrounding layout use this so the same module works in tests.
 */
export function useOptionalAppShellLayoutContext() {
  return useContext(AppShellLayoutContext)
}

/**
 * Best-effort active chat id for callers that want to round-trip through
 * `/v2/artifacts/access` to renew sandbox tokens without taking on the
 * AppShell dependency. Returns `null` outside the AppShell tree.
 */
export function useOptionalActiveChatId() {
  const layout = useOptionalAppShellLayoutContext()
  if (!layout) return null
  const nav = layout?.secondaryNavProps
  if (!nav || typeof nav !== 'object') return null
  const selected = nav.selectedThreadId
  return typeof selected === 'string' && selected ? selected : null
}
